<template>
    <div class="page-editor mx-2">
        <div class="page-editor-overview pb-5">
        	<h1>{{local_page.id ? 'Update' : 'Add'}} Static Page</h1>
         
            <div class="card mt-3">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-9 col mb-3">
                            <label class="form-label">Headline</label>
                            <input 
                                type="text" 
                                id="title-input" 
                                class="form-control" 
                                v-model="local_page.title"
                                :class="errors.title ? 'is-invalid' : ''" 
                                @change="clearErrorForField('title')"
                             aria-label="Headline">
                            <div class="text-danger invalid-feedback" v-if="errors.title">
                                {{errors.title}}
                            </div>
                        </div>
                        <div class="mb-3 col-sm-3">
                            <label class="form-label">Slug</label>
                            <input 
                                type="text" 
                                id="slug-input" 
                                class="form-control" 
                                v-model="local_page.slug"
                                :class="errors.slug ? 'is-invalid' : ''" 
                                @change="clearErrorForField('slug')"
                             aria-label="Slug">
                            <small class="text-muted">Leave blank to auto-generate from title</small>
                            <div class="text-danger invalid-feedback" v-if="errors.slug">
                                {{errors.slug}}
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Description (WYSIWYG)</label>
                            <QuillyEditor 
                                ref="descriptionEditor"
                                v-model="local_page.description" 
                                :class="errors.description ? 'is-invalid' : ''" 
                                @update:modelValue="clearErrorForField('description')"
                                :options="editorOptions"
                            ></QuillyEditor>
                            <small class="text-muted">This should be written in paragraph form to describe the problem that this section solves.</small>
                            <div class="text-danger invalid-feedback" v-if="errors.description">
                                {{errors.description}}
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Embed URL</label>
                            <input 
                                id="embed-url" 
                                class="form-control" 
                                :class="errors.embed_url ? 'is-invalid' : ''"
                                v-model="local_page.embed_url" 
                                @change="clearErrorForField('embed_url')"
                             aria-label="Embed URL">
                            <div class="text-danger invalid-feedback" v-if="errors.embed_url">
                                {{errors.embed_url}}
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Features (WYSIWYG)</label>
                            <QuillyEditor 
                                ref="featuresEditor"
                                v-model="local_page.features" 
                                :class="errors.features ? 'is-invalid' : ''" 
                                @update:modelValue="clearErrorForField('features')"
                                :options="editorOptions"
                            ></QuillyEditor>
                            <small class="text-muted">Make a bullet pointed list of the features in this section.</small>
                            <div class="text-danger invalid-feedback" v-if="errors.features">
                                {{errors.features}}
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Call to Action (WYSIWYG)</label>
                            <input 
                                v-model="local_page.cta" 
                                :class="errors.cta ? 'is-invalid' : ''" 
                                @change="clearErrorForField('cta')" 
                                class="form-control" aria-label="Call to Action (WYSIWYG)">
                            <small class="text-muted">The text to display above the Book a Demo button.</small>
                            <div class="text-danger invalid-feedback" v-if="errors.cta">
                                {{errors.cta}}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-12 navbar navbar-expand">
                    <button 
                        type="submit" 
                        class="btn btn-primary ms-2" 
                        id="save-button" 
                        @click.prevent="update" 
                        :disabled="form.busy"
                    >
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save 
                    </button>
                    <a href="/admin/pages" class="btn btn-secondary ms-2">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import { QuillyEditor } from 'vue-quilly';
import Quill from 'quill';

export default {
    name: 'PageEditor',
    components: { 
        QuillyEditor 
    },
    props: {
        page: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            local_page: {},
            errors: {
                title: '',
                slug: '',
                description: '',
                embed_url: '',
                features: '',
                cta: '',
            },
            form: {
                successful: false,
                busy: false,
                error: false 
            },
            if_changes_made: false,
            editorOptions: {
                theme: 'snow',
                modules: {
                    toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['image', 'link']
                    ]
                },
                readOnly: false
            }
        };
    },
    beforeMount() {
        this.local_page = { ...this.page };
    },
    mounted() {
        // Initialize Quill editors
        this.$nextTick(() => {
            if (this.$refs.descriptionEditor) {
                this.$refs.descriptionEditor.initialize(Quill);
            }
            if (this.$refs.featuresEditor) {
                this.$refs.featuresEditor.initialize(Quill);
            }
            
            // Add tooltips to Quill toolbar buttons
            this.initializeQuillTooltips();
        });
    },
    methods: {
        clearErrors() {
            this.errors = {
                title: '',
                slug: '',
                description: '',
                embed_url: '',
                features: '',
                cta: '',
            }
        },
        clearErrorForField(field_name){
            this.errors[field_name] = '';
        },     
        hasErrors() {
            // Reset previous errors
            this.clearErrors();
            
            // Error checking for required fields
            if(!this.local_page.title){
                this.errors.title = "A title is required.";
            }
            
            return Object.values(this.errors).some(error => error !== "");
        },
        update(){
            if(this.hasErrors()) {
                Swal.fire({
                    title: "Error Saving Page",
                    text: "There were some issues saving this page. Please review and make the suggested changes.",
                    icon: "error",
                    confirmButtonText: "View Errors",
                    customClass: {
                        popup: 'swal-error'
                    },
                });
                return;
            }

            this.form.busy = true;

            const payload = {
                title: this.local_page.title,
                slug: this.local_page.slug,
                description: this.local_page.description,
                embed_url: this.local_page.embed_url,
                features: this.local_page.features,
                cta: this.local_page.cta,
            };

            if(this.local_page.id) {
                // Update existing Page
                window.axios.put(`/api/pages/${this.local_page.id}`, payload)
                    .then(response => {
                        this.local_page = response.data.data;
                        this.if_changes_made = false;
                        this.form.busy = false;
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: "The page has been updated successfully!",
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            window.location.href = '/admin/pages';
                        });
                    })
                    .catch(error => {
                        this.form.busy = false;
                        if (error.response && error.response.status === 422) {
                            // Handle validation errors
                            this.errors = error.response.data.errors;
                            Swal.fire({
                                title: "Validation Error",
                                text: "Please correct the highlighted errors and try again.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        } else {
                            // Handle other errors
                            this.form.error = true;
                            Swal.fire({
                                title: "Error",
                                text: "An unexpected error occurred while updating the page.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                    });
            } else {
                // Create a new Page
                window.axios.post('/api/pages', payload)
                    .then(response => {
                        this.local_page = response.data.data;
                        this.if_changes_made = false;
                        this.form.busy = false;
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: "The page has been created successfully!",
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            window.location.href = '/admin/pages';
                        });
                    })
                    .catch(error => {
                        this.form.busy = false;
                        if (error.response && error.response.status === 422) {
                            // Handle validation errors
                            this.errors = error.response.data.errors;
                            Swal.fire({
                                title: "Validation Error",
                                text: "Please correct the highlighted errors and try again.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        } else {
                            // Handle other errors
                            this.form.error = true;
                            Swal.fire({
                                title: "Error",
                                text: "An unexpected error occurred while creating the page.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                    });
            }
        },
        initializeQuillTooltips() {
                const toolbarButtons = [
                    { selector: '.ql-header[value="1"]', title: 'Heading 1' },
                    { selector: '.ql-header[value="2"]', title: 'Heading 2' },
                    { selector: '.ql-header[value="3"]', title: 'Heading 3' },
                    { selector: '.ql-bold', title: 'Bold' },
                    { selector: '.ql-italic', title: 'Italic' },
                    { selector: '.ql-underline', title: 'Underline' },
                    { selector: '.ql-list[value="ordered"]', title: 'Numbered List' },
                    { selector: '.ql-list[value="bullet"]', title: 'Bulleted List' },
                    { selector: '.ql-image', title: 'Insert Image' },
                    { selector: '.ql-link', title: 'Insert Link' }
                ];

                toolbarButtons.forEach(({ selector, title }) => {
                    const buttons = document.querySelectorAll(selector);
                    buttons.forEach(button => {
                        button.setAttribute('data-bs-toggle', 'tooltip');
                        button.setAttribute('data-bs-placement', 'bottom');
                        button.setAttribute('title', title);
                    });
                });

                // Initialize tooltips
                const tooltipTriggerList = document.querySelectorAll('.ql-toolbar [data-bs-toggle="tooltip"]');
                [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
        }
    }
}
</script> 